import { Component, inject, input, model, OnInit, output, signal } from '@angular/core';
import { PrintOfficeService } from '../../../services/print-office.service';
import { PrintOfficeDto } from '../../../models/server-models/entities/print-office-dto';
import { Availability } from '../../../models/server-models/enums/availability';
import { UploadedFile } from '../../../models/server-models/entities/uploaded-file';
import { ExpandedPrintVariation } from '../../../models/client-models/entities/expanded-print-variation';
import { ExpandedPaperSize } from '../../../models/client-models/entities/expanded-paper-size';
import { ExpandedPaperType } from '../../../models/client-models/entities/expanded-paper-type';
import { PaperSideType } from '../../../models/server-models/enums/paper-side-type';
import { ExpandedPaperExtra } from '../../../models/client-models/entities/expanded-paper-extra';
import { PaperColorType } from '../../../models/server-models/enums/paper-color-type';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../../../shared/services/error.service';
import {
  GetOfficesOffersRequestDto,
  OfferFile,
} from '../../../models/server-models/request-dtos/get-offices-offers-request-dto';
import { OfferPricingSummary } from '../../../models/server-models/reseponse-dtos/get-offices-offers-response.dto';
import { ModalController } from '@ionic/angular';
import { OfferDetailsModalComponent } from '../offer-details-modal/offer-details-modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-print-office-selection',
  templateUrl: './print-office-selection.component.html',
  styleUrls: ['./print-office-selection.component.scss'],
  standalone: false,
})
export class PrintOfficeSelectionComponent implements OnInit {
  printOfficeService = inject(PrintOfficeService);
  errorService = inject(ErrorService);
  modalCtrl = inject(ModalController);

  allPrintOffices = input.required<PrintOfficeDto[] | null>();
  printOfficesWithOffer = signal<PrintOfficeWithOfferDto[]>([]);

  selectedVariation = input.required<ExpandedPrintVariation | null>();
  selectedPaperSize = input.required<ExpandedPaperSize | null>();
  selectedPaperType = input.required<ExpandedPaperType | null>();
  selectedPaperSideType = input.required<PaperSideType | null>();
  selectedPaperColorType = input.required<PaperColorType | null>();
  selectedExtras = input.required<ExpandedPaperExtra[]>();
  uploadedFiles = input.required<UploadedFile[]>();

  private destroySubscription: Subscription | null = null;

  doNextStep = output<void>();
  doBackStep = output<void>();

  fakeOffices = signal<PrintOfficeDto[]>([this.fakeOffice(), this.fakeOffice(), this.fakeOffice()]);

  loading = signal<boolean>(true);

  selectedOffice = model<PrintOfficeWithOfferDto | null>(null);

  constructor() {}

  ngOnInit() {
    this.loadOffices();
  }

  ngOnDestroy() {
    this.destroySubscription?.unsubscribe();
  }

  onOfficeClick(office: PrintOfficeWithOfferDto) {
    this.selectedOffice.set(office);
  }

  loadOffices() {
    this.loading.set(true);

    this.destroySubscription = this.printOfficeService
      .getOfficeOffers(<GetOfficesOffersRequestDto>{
        printVariationId: this.selectedVariation()!.id,
        paperSizeId: this.selectedPaperSize()!.id,
        paperTypeId: this.selectedPaperType()!.id,
        paperSideType: this.selectedPaperSideType()!,
        paperColorType: this.selectedPaperColorType()!,
        paperExtraIds: this.selectedExtras().map((e) => e.id),

        offerFiles: this.uploadedFiles().map(
          (f) =>
            <OfferFile>{
              uniqueFileName: f.uniqueFileName,
              copies: f.copies,
            }
        ),
      })
      .subscribe({
        next: (response) => {
          const offices = this.allPrintOffices()!
            .filter((o) => Object.keys(response).some((key) => key === o.id))
            .map((o) => {
              return <PrintOfficeWithOfferDto>{
                ...o,
                offeredPrice: response[o.id].printTotalPrice,
                offerDetails: response[o.id],
              };
            });
          this.printOfficesWithOffer.set(offices);

          if (this.printOfficesWithOffer().length == 1) {
            this.selectedOffice.set(this.printOfficesWithOffer()[0]);
          }

          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error);

          this.doBackStep.emit();

          this.loading.set(false);
        },
      });
  }

  getPrintPriceWithCommission(office: PrintOfficeWithOfferDto) {
    return office.offerDetails.totalPrice;
  }

  fakeOffice() {
    return {
      id: '1',
      name: { en: '', ge: '', ru: '' },
      address: { en: '', ge: '', ru: '' },
      priority: 1,
      availability: Availability.Available,
      printVariations: [],
    };
  }

  async openOfferModal() {
    const modal = await this.modalCtrl.create({
      component: OfferDetailsModalComponent,
      componentProps: {
        selectedOffice: this.selectedOffice(),
        printVariation: this.selectedVariation(),
      },
      cssClass: 'custom-modal-width',
      breakpoints: [0, 0.5, 1], // Supports draggable sheet modals
      initialBreakpoint: 1,
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (data?.continue) {
      this.doNextStep.emit();
    }
  }
}

export type PrintOfficeWithOfferDto = PrintOfficeDto & {
  offerDetails: OfferPricingSummary;
};
